import { PlusIcon } from '@langchain/untitled-ui-icons';
import { Button, LinearProgress } from '@mui/joy';

import { Dispatch, FC, RefObject, SetStateAction, useState } from 'react';

import Chip from '@/components/Chip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { EQuickAdjustment } from '@/hooks/promptCanvas/types';

import {
  ECreateActionModalState,
  useCanvasQuickActions,
} from '../hooks/useCanvasQuickActions';
import CustomQuickAction from './CustomQuickAction';
import CustomQuickActionPopover from './CustomQuickActionPopover';

type TProps = {
  onSubmit: (body: { customAction?: string }) => void;
  disabled: boolean;
  customQuickActionButtonRef: RefObject<HTMLDivElement>;
  customQuickActionListRef: RefObject<HTMLDivElement>;
  customQuickActionPopoverRef: RefObject<HTMLDivElement>;
  quickAdjustmentType: EQuickAdjustment | undefined;
  setQuickAdjustmentType: Dispatch<
    SetStateAction<EQuickAdjustment | undefined>
  >;
};

const CustomQuickActionList: FC<TProps> = ({
  onSubmit,
  disabled,
  customQuickActionButtonRef,
  customQuickActionListRef,
  customQuickActionPopoverRef,
  quickAdjustmentType,
  setQuickAdjustmentType,
}) => {
  const [isCreateActionPopoverOpen, setIsCreateActionPopoverOpen] =
    useState(false);

  const isListOpen = quickAdjustmentType === EQuickAdjustment.CUSTOM;

  const { data: quickActions, isLoading, error } = useCanvasQuickActions();

  const renderQuickActions = () => {
    if (isLoading) {
      return <LinearProgress />;
    }

    if (error) {
      return (
        <span className="text-sm text-error">Error loading custom actions</span>
      );
    }
    if (!quickActions || quickActions.length === 0)
      return <span className="text-sm text-secondary">No custom actions.</span>;

    const sortedQuickActions = quickActions?.sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    return (
      <>
        {sortedQuickActions.map((action) => (
          <div key={action.id} className="shrink-0">
            <CustomQuickAction
              action={action}
              onSubmit={(body) => {
                onSubmit(body);
                setQuickAdjustmentType(undefined);
              }}
              disabled={disabled}
            />
          </div>
        ))}
      </>
    );
  };

  const renderAddQuickActionButton = () => {
    return (
      <Popover
        open={isCreateActionPopoverOpen}
        onOpenChange={setIsCreateActionPopoverOpen}
      >
        <PopoverTrigger className="w-full shrink-0">
          <div className="mt-2 w-full">
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setIsCreateActionPopoverOpen(true)}
              className="w-full"
              disabled={disabled}
            >
              <div className="flex flex-row items-center gap-1">
                <PlusIcon className="h-4 w-4" />
                Add custom action
              </div>
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="h-[400px] w-[400px]"
          ref={customQuickActionPopoverRef}
        >
          <CustomQuickActionPopover
            onClose={() => setIsCreateActionPopoverOpen(false)}
            modalState={ECreateActionModalState.CREATE}
            quickAction={undefined}
          />
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <Popover open={isListOpen}>
      <PopoverTrigger>
        <Chip
          label="Custom actions"
          onClick={() => setQuickAdjustmentType(EQuickAdjustment.CUSTOM)}
          ref={customQuickActionButtonRef}
        />
      </PopoverTrigger>
      <PopoverContent className="border-none">
        <div className="flex flex-col gap-1" ref={customQuickActionListRef}>
          {renderQuickActions()}
          {renderAddQuickActionButton()}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CustomQuickActionList;
